/**
 * WebhookManager — Webhook endpoint CRUD, test, delivery log
 * Phase 4.7.5 (WQ-170)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listWebhooks,
  createWebhook,
  deleteWebhook,
  testWebhook,
  getDeliveries,
  updateWebhook,
  type WebhookEndpoint,
  type WebhookDelivery,
} from '../../services/webhookService';

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function healthColor(ep: WebhookEndpoint): string {
  if (!ep.is_active) return '#dc2626';
  if (ep.consecutive_failures >= 5) return '#dc2626';
  if (ep.consecutive_failures >= 1) return '#eab308';
  if (ep.last_delivery_status === 'delivered') return '#22c55e';
  return '#6b7280';
}

const WebhookManager: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formSeverity, setFormSeverity] = useState('low');

  const fetchEndpoints = useCallback(async () => {
    try {
      const data = await listWebhooks(authFetch);
      setEndpoints(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => {
    if (!authLoading) fetchEndpoints();
  }, [authLoading, fetchEndpoints]);

  const handleCreate = async () => {
    try {
      const ep = await createWebhook(authFetch, { name: formName, url: formUrl, min_severity: formSeverity });
      setNewSecret(ep.secret_plaintext || null);
      setShowCreate(false);
      setFormName('');
      setFormUrl('');
      fetchEndpoints();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to create webhook');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Disable this webhook endpoint?')) return;
    try {
      await deleteWebhook(authFetch, id);
      fetchEndpoints();
    } catch { /* silent */ }
  };

  const handleTest = async (id: string) => {
    setTestResult(null);
    try {
      const result = await testWebhook(authFetch, id);
      setTestResult(result);
    } catch (e) {
      setTestResult({ error: e instanceof Error ? e.message : 'Test failed' });
    }
  };

  const handleReEnable = async (id: string) => {
    try {
      await updateWebhook(authFetch, id, { is_active: true } as Partial<WebhookEndpoint>);
      fetchEndpoints();
    } catch { /* silent */ }
  };

  const handleViewDeliveries = async (id: string) => {
    if (selectedId === id) { setSelectedId(null); return; }
    setSelectedId(id);
    try {
      const data = await getDeliveries(authFetch, id);
      setDeliveries(data);
    } catch { setDeliveries([]); }
  };

  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          Webhook Endpoints
        </h3>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: '6px 14px', borderRadius: '6px',
            background: 'var(--accent-primary, #3b82f6)', color: '#fff',
            border: 'none', cursor: 'pointer', fontSize: '13px',
          }}
        >
          + Register Endpoint
        </button>
      </div>

      {/* Secret display */}
      {newSecret && (
        <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)', border: '1px solid #22c55e' }}>
          <div style={{ fontWeight: 600, color: '#22c55e', marginBottom: '8px' }}>Webhook Secret (shown once)</div>
          <code style={{ display: 'block', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', wordBreak: 'break-all', fontSize: '13px' }}>
            {newSecret}
          </code>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button onClick={() => navigator.clipboard.writeText(newSecret)} style={{ ...smallBtn, borderColor: '#22c55e', color: '#22c55e' }}>Copy</button>
            <button onClick={() => setNewSecret(null)} style={{ ...smallBtn, borderColor: '#6b7280', color: '#6b7280' }}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <h4 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>Register New Webhook</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Name" style={inputStyle} />
            <input value={formUrl} onChange={(e) => setFormUrl(e.target.value)} placeholder="https://example.com/webhook" style={inputStyle} />
            <select value={formSeverity} onChange={(e) => setFormSeverity(e.target.value)} style={inputStyle}>
              {['low', 'medium', 'high', 'critical'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleCreate} disabled={!formName || !formUrl} style={{ ...smallBtn, background: 'var(--accent-primary)', color: '#fff', borderColor: 'var(--accent-primary)' }}>Create</button>
              <button onClick={() => setShowCreate(false)} style={smallBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Endpoint list */}
      {loading ? (
        <div style={{ padding: '20px', color: 'var(--text-muted)' }}>Loading...</div>
      ) : endpoints.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          No webhook endpoints registered.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                {['Health', 'Name', 'URL', 'Events', 'Last Delivery', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {endpoints.map((ep) => (
                <React.Fragment key={ep.id}>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: healthColor(ep) }} />
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{ep.name}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ep.url}>{ep.url}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {ep.event_filter.length === 0 ? 'All events' : `${ep.event_filter.length} types`}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>{relativeTime(ep.last_delivery_at)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleTest(ep.id)} style={smallBtn}>Test</button>
                        <button onClick={() => handleViewDeliveries(ep.id)} style={smallBtn}>{selectedId === ep.id ? 'Hide' : 'Log'}</button>
                        {!ep.is_active ? (
                          <button onClick={() => handleReEnable(ep.id)} style={{ ...smallBtn, borderColor: '#22c55e', color: '#22c55e' }}>Re-enable</button>
                        ) : (
                          <button onClick={() => handleDelete(ep.id)} style={{ ...smallBtn, borderColor: '#dc2626', color: '#dc2626' }}>Disable</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {selectedId === ep.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px', background: 'rgba(0,0,0,0.15)' }}>
                        <h5 style={{ margin: '0 0 8px', color: 'var(--text-secondary)', fontSize: '13px' }}>Recent Deliveries</h5>
                        {deliveries.length === 0 ? (
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>No deliveries yet.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                {['Attempt', 'Status', 'HTTP', 'Latency', 'Time'].map((h) => (
                                  <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontSize: '11px', color: 'var(--text-muted)' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {deliveries.slice(0, 10).map((d) => (
                                <tr key={d.id}>
                                  <td style={{ padding: '4px 8px', fontSize: '12px' }}>{d.attempt}</td>
                                  <td style={{ padding: '4px 8px', fontSize: '12px', color: d.status === 'delivered' ? '#22c55e' : '#dc2626' }}>{d.status}</td>
                                  <td style={{ padding: '4px 8px', fontSize: '12px' }}>{d.http_status || '-'}</td>
                                  <td style={{ padding: '4px 8px', fontSize: '12px' }}>{d.latency_ms ? `${d.latency_ms}ms` : '-'}</td>
                                  <td style={{ padding: '4px 8px', fontSize: '12px', color: 'var(--text-muted)' }}>{relativeTime(d.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div className="card" style={{ padding: 'var(--space-3)', marginTop: 'var(--space-3)', fontSize: '12px' }}>
          <strong>Test Result:</strong> <code>{JSON.stringify(testResult)}</code>
          <button onClick={() => setTestResult(null)} style={{ ...smallBtn, marginLeft: '8px' }}>Dismiss</button>
        </div>
      )}
    </div>
  );
};

const smallBtn: React.CSSProperties = {
  padding: '3px 8px',
  borderRadius: '4px',
  border: '1px solid var(--glass-border)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: '11px',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass-bg)',
  color: 'var(--text-primary)',
  fontSize: '13px',
};

export default WebhookManager;
